class CheckoutDrawerFlow {
  constructor() {
    this.currentStep = 0;
    this.isGift = false;
    this.deliveryDate = null;
    this.calendarDate = new Date();
    this.calendarDate.setDate(1); // Anchor to 1st to prevent setMonth overflow
    this.giftingSteps = [
      'GiftQuestionDrawer',
      'GiftDetailsDrawer',
      'GiftMessageDrawer',
      'DeliveryDateDrawer',
      'AlmostCompleteDrawer',
      'OrderSummaryDrawer'
    ];
    this.nonGiftingSteps = [
      'GiftQuestionDrawer',
      'GiftMessageDrawer',
      'DeliveryDateDrawer',
      'AlmostCompleteDrawer',
      'OrderSummaryDrawer'
    ];
    this.steps = this.nonGiftingSteps; // Default to non-gifting
    this.boundHandlers = {}; // Store bound handlers for cleanup
    this.init();
  }

  init() {
    // Clean up old listeners before adding new ones
    this.cleanup();

    // Handle cart "Next" button
    const cartNextBtn = document.querySelector('[data-cart-next]');
    if (cartNextBtn) {
      this.boundHandlers.startFlow = () => this.startFlow();
      cartNextBtn.addEventListener('click', this.boundHandlers.startFlow);
    }

    // Handle gift question buttons
    const giftYesBtn = document.querySelector('[data-gift-yes]');
    const giftNoBtn = document.querySelector('[data-gift-no]');

    if (giftYesBtn) {
      this.boundHandlers.giftYes = () => this.handleGiftChoice(true);
      giftYesBtn.addEventListener('click', this.boundHandlers.giftYes);
    }
    if (giftNoBtn) {
      this.boundHandlers.giftNo = () => this.handleGiftChoice(false);
      giftNoBtn.addEventListener('click', this.boundHandlers.giftNo);
    }

    // Handle delivery date selection (mobile native input)
    const dateInputMobile = document.getElementById('delivery-date-input-mobile');
    if (dateInputMobile) {
      this.boundHandlers.dateChangeMobile = (e) => {
        this.deliveryDate = e.target.value;
        // Sync to hidden input
        const hiddenInput = document.getElementById('delivery-date-input');
        if (hiddenInput) hiddenInput.value = e.target.value;
      };
      dateInputMobile.addEventListener('change', this.boundHandlers.dateChangeMobile);
    }

    // Initialize custom calendar for desktop
    this.initCalendar();

    // Handle receiver type toggle
    const receiverType = document.getElementById('receiver-type');
    if (receiverType) {
      this.boundHandlers.receiverTypeChange = (e) => {
        this.toggleSenderSection(e.target.value);
      };
      receiverType.addEventListener('change', this.boundHandlers.receiverTypeChange);
    }

    // Handle next/previous buttons using event delegation on document
    this.boundHandlers.documentClick = (e) => {
      if (e.target.closest('[data-drawer-next]')) {
        this.handleNext();
      }
      if (e.target.closest('[data-gift-details-next]')) {
        this.handleGiftDetailsNext();
      }
      if (e.target.closest('[data-drawer-previous]')) {
        this.goToPrevious();
      }
      if (e.target.closest('[data-close-drawer]')) {
        this.closeEverything();
      }
      if (e.target.closest('[data-upsell-add]')) {
        this.handleUpsellAdd(e.target.closest('[data-upsell-add]'));
      }
      // Order Summary customisation handlers
      if (e.target.closest('[data-add-customisation]') || e.target.closest('[data-edit-customisation]')) {
        const wrapper = e.target.closest('[data-customisation-wrapper]');
        if (wrapper) this.showCustomisationForm(wrapper);
      }
      if (e.target.closest('[data-cancel-customisation]')) {
        const wrapper = e.target.closest('[data-customisation-wrapper]');
        if (wrapper) this.hideCustomisationForm(wrapper);
      }
      if (e.target.closest('[data-save-customisation]')) {
        const item = e.target.closest('.order-summary__item');
        const wrapper = e.target.closest('[data-customisation-wrapper]');
        if (item && wrapper) this.saveCustomisation(item, wrapper);
      }
    };
    document.addEventListener('click', this.boundHandlers.documentClick);

    // Character counter for customisation textarea
    this.boundHandlers.documentInput = (e) => {
      if (e.target.matches('[data-customisation-input]')) {
        const wrapper = e.target.closest('[data-customisation-wrapper]');
        const counter = wrapper?.querySelector('[data-customisation-counter]');
        if (counter) {
          counter.textContent = `${e.target.value.length}/30`;
        }
      }
      // Character counter for gift message textarea
      if (e.target.matches('[data-gift-message-input]')) {
        const counter = document.querySelector('[data-gift-message-counter]');
        if (counter) {
          counter.textContent = `${e.target.value.length}/200`;
        }
      }
    };
    document.addEventListener('input', this.boundHandlers.documentInput);
  }

  cleanup() {
    // Remove old event listeners to prevent duplicates
    const cartNextBtn = document.querySelector('[data-cart-next]');
    if (cartNextBtn && this.boundHandlers.startFlow) {
      cartNextBtn.removeEventListener('click', this.boundHandlers.startFlow);
    }

    const giftYesBtn = document.querySelector('[data-gift-yes]');
    if (giftYesBtn && this.boundHandlers.giftYes) {
      giftYesBtn.removeEventListener('click', this.boundHandlers.giftYes);
    }

    const giftNoBtn = document.querySelector('[data-gift-no]');
    if (giftNoBtn && this.boundHandlers.giftNo) {
      giftNoBtn.removeEventListener('click', this.boundHandlers.giftNo);
    }

    const dateInput = document.getElementById('delivery-date-input');
    if (dateInput && this.boundHandlers.dateChange) {
      dateInput.removeEventListener('change', this.boundHandlers.dateChange);
    }

    const receiverType = document.getElementById('receiver-type');
    if (receiverType && this.boundHandlers.receiverTypeChange) {
      receiverType.removeEventListener('change', this.boundHandlers.receiverTypeChange);
    }

    if (this.boundHandlers.documentClick) {
      document.removeEventListener('click', this.boundHandlers.documentClick);
    }
  }

  reinit() {
    // Reinitialize event listeners after cart content updates
    this.init();
  }

  resetState() {
    // Reset all checkout flow state - called when cart is cleared
    this.currentStep = 0;
    this.isGift = false;
    this.deliveryDate = null;
    this.calendarDate = new Date();
    this.calendarDate.setDate(1);
    this.steps = this.nonGiftingSteps;

    // Clear form inputs
    const giftMessageInput = document.getElementById('gift-message-input');
    if (giftMessageInput) giftMessageInput.value = '';

    const giftMessageCounter = document.querySelector('[data-gift-message-counter]');
    if (giftMessageCounter) giftMessageCounter.textContent = '0/200';

    const noCardCheckbox = document.getElementById('no-card-needed');
    if (noCardCheckbox) noCardCheckbox.checked = false;

    const giftDetailsForm = document.getElementById('GiftDetailsForm');
    if (giftDetailsForm) giftDetailsForm.reset();

    const deliveryDateInput = document.getElementById('delivery-date-input');
    if (deliveryDateInput) deliveryDateInput.value = '';

    const deliveryDateInputMobile = document.getElementById('delivery-date-input-mobile');
    if (deliveryDateInputMobile) deliveryDateInputMobile.value = '';

    // Reset receiver/sender sections visibility
    const receiverSection = document.getElementById('receiver-section');
    const senderSection = document.getElementById('sender-section');
    if (receiverSection) receiverSection.style.display = 'none';
    if (senderSection) senderSection.style.display = 'none';

    // Re-render calendar to clear selection
    this.renderCalendar();
  }

  startFlow() {
    // Keep cart drawer open - don't close it
    // Start at first step
    this.currentStep = 0;
    this.openDrawer(this.steps[0]);
  }

  async handleGiftChoice(isGift) {
    this.isGift = isGift;

    // Set the appropriate flow based on gift choice
    this.steps = isGift ? this.giftingSteps : this.nonGiftingSteps;

    if (isGift) {
      // Save gift choice to cart attributes
      await this.updateCartAttributes({
        'gift_order': 'true'
      });
    } else {
      // Clear all gift-related attributes but keep gift_order as false
      await this.clearGiftAttributes(true);
      await this.updateCartAttributes({
        'gift_order': 'false'
      });
    }

    this.goToNext();
  }

  toggleSenderSection(receiverType) {
    const senderSection = document.getElementById('sender-section');
    const receiverSection = document.getElementById('receiver-section');

    if (receiverType === 'myself') {
      // If receiving myself, hide both sections - address comes from checkout
      if (receiverSection) {
        receiverSection.style.display = 'none';
        receiverSection.querySelectorAll('[required]').forEach(field => {
          field.removeAttribute('required');
        });
      }
      if (senderSection) {
        senderSection.style.display = 'none';
        senderSection.querySelectorAll('[required]').forEach(field => {
          field.removeAttribute('required');
        });
      }
    } else if (receiverType === 'someone-else') {
      // If someone else is receiving, show delivery address and sender info
      if (receiverSection) {
        receiverSection.style.display = 'block';
        document.getElementById('receiver-first-name')?.setAttribute('required', '');
        document.getElementById('receiver-last-name')?.setAttribute('required', '');
        document.getElementById('receiver-phone')?.setAttribute('required', '');
      }
      if (senderSection) {
        senderSection.style.display = 'block';
        document.getElementById('sender-first-name')?.setAttribute('required', '');
        document.getElementById('sender-last-name')?.setAttribute('required', '');
        document.getElementById('sender-phone')?.setAttribute('required', '');
      }
    }
  }

  async handleGiftDetailsNext() {
    const form = document.getElementById('GiftDetailsForm');
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    // Collect form data
    const formData = new FormData(form);
    const giftDetails = {};
    formData.forEach((value, key) => {
      giftDetails[key] = value;
    });

    // Save to cart attributes
    await this.updateCartAttributes(giftDetails);

    this.goToNext();
  }

  async handleNext() {
    // If on delivery date step, save the date
    if (this.steps[this.currentStep] === 'DeliveryDateDrawer') {
      if (!this.deliveryDate) {
        this.showDateError('Please select a delivery date');
        return;
      }

      // Validate date against restrictions
      if (window.deliveryRestrictions) {
        const selectedDate = new Date(this.deliveryDate + 'T00:00:00');
        if (!window.deliveryRestrictions.isDateAllowed(selectedDate)) {
          const message = window.deliveryRestrictions.getBlockedDateMessage(selectedDate) ||
                          'This date is not available for delivery';
          window.deliveryRestrictions.showDateError(message);
          return;
        }
      }

      // Clear any error messages on successful validation
      this.clearDateError();

      await this.updateCartAttributes({
        'delivery_date': this.deliveryDate
      });
    }

    // If on gift message step, save the message and no card preference
    if (this.steps[this.currentStep] === 'GiftMessageDrawer') {
      const giftMessage = document.getElementById('gift-message-input').value;
      const noCardCheckbox = document.getElementById('no-card-needed');
      const noCardNeeded = noCardCheckbox ? noCardCheckbox.checked : false;

      const attributes = {};
      if (giftMessage) {
        attributes['gift_message'] = giftMessage;
      }
      attributes['no_card_needed'] = noCardNeeded ? 'true' : 'false';

      await this.updateCartAttributes(attributes);
    }

    this.goToNext();
  }

  async updateCartAttributes(attributes) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ attributes })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart attributes');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating cart attributes:', error);
      alert('Failed to save. Please try again.');
      throw error;
    }
  }

  async clearGiftAttributes(keepGiftOrder = false) {
    // Set all gift-related attributes to empty string to clear them
    const clearAttributes = {
      'receiver_type': '',
      'receiver_first_name': '',
      'receiver_last_name': '',
      'receiver_phone': '',
      'sender_first_name': '',
      'sender_last_name': '',
      'sender_phone': '',
      'additional_notes': '',
      'gift_message': '',
      'no_card_needed': ''
    };

    // Only clear gift_order if going back to gift question
    if (!keepGiftOrder) {
      clearAttributes.gift_order = '';
    }

    try {
      await this.updateCartAttributes(clearAttributes);
      console.log('Gift attributes cleared');
    } catch (error) {
      console.error('Error clearing gift attributes:', error);
    }
  }

  async clearAllCartAttributes() {
    // Clear all checkout-related cart attributes when cart is emptied
    const clearAttributes = {
      'gift_order': '',
      'receiver_type': '',
      'receiver_first_name': '',
      'receiver_last_name': '',
      'receiver_phone': '',
      'sender_first_name': '',
      'sender_last_name': '',
      'sender_phone': '',
      'additional_notes': '',
      'gift_message': '',
      'no_card_needed': '',
      'delivery_date': ''
    };

    try {
      await this.updateCartAttributes(clearAttributes);
    } catch (error) {
      // Silent fail - cart is already empty
      console.error('Error clearing cart attributes:', error);
    }
  }

  goToNext() {
    this.currentStep++;
    if (this.currentStep < this.steps.length) {
      this.openDrawer(this.steps[this.currentStep]);
    }
  }

  async goToPrevious() {
    // Close current drawer
    if (this.currentStep >= 0 && this.currentStep < this.steps.length) {
      this.closeDrawer(this.steps[this.currentStep]);
    }

    this.currentStep--;

    // If going back to gift question, clear all gift attributes
    if (this.currentStep >= 0 && this.steps[this.currentStep] === 'GiftQuestionDrawer') {
      await this.clearGiftAttributes();
    }

    if (this.currentStep < 0) {
      // Close the first checkout drawer to reveal cart
      this.closeDrawer(this.steps[0]);
    }
  }

  openDrawer(drawerId) {
    const drawer = document.getElementById(drawerId);
    if (drawer) {
      // Set step number for CSS width calculation (1-indexed for display)
      drawer.setAttribute('data-step', this.currentStep + 1);

      drawer.removeAttribute('hidden');
      // Small delay to allow hidden removal to take effect before animation
      requestAnimationFrame(() => {
        drawer.classList.add('active');

        // Scroll all potential scrollable elements to top
        const inner = drawer.querySelector('.drawer__inner');
        const contents = drawer.querySelector('.drawer__contents');
        const form = drawer.querySelector('form');

        if (inner) inner.scrollTop = 0;
        if (contents) contents.scrollTop = 0;
        if (form) form.scrollTop = 0;

        // Also scroll the drawer itself
        drawer.scrollTop = 0;
      });

      // Refresh data when opening specific drawers
      if (drawerId === 'AlmostCompleteDrawer') {
        this.updateFreeShippingBar();
      }
      if (drawerId === 'OrderSummaryDrawer') {
        this.refreshOrderSummary();
        // Initialize scroll fade indicators
        setTimeout(() => this.initScrollFadeIndicators(drawer), 100);
      }
    }
  }

  initScrollFadeIndicators(drawer) {
    const itemsWrapper = drawer.querySelector('.order-summary__items-wrapper');
    const itemsScroll = drawer.querySelector('.order-summary__items');
    const bottomWrapper = drawer.querySelector('.order-summary__bottom-wrapper');
    const bottomScroll = drawer.querySelector('.order-summary__bottom-inner');

    const checkOverflow = (wrapper, scrollEl) => {
      if (!wrapper || !scrollEl) return;

      const hasOverflow = scrollEl.scrollHeight > scrollEl.clientHeight;
      const isAtBottom = scrollEl.scrollHeight - scrollEl.scrollTop <= scrollEl.clientHeight + 5;

      wrapper.classList.toggle('has-overflow', hasOverflow);
      wrapper.classList.toggle('scrolled-to-bottom', isAtBottom);
    };

    // Initial check
    checkOverflow(itemsWrapper, itemsScroll);
    checkOverflow(bottomWrapper, bottomScroll);

    // Listen for scroll events
    if (itemsScroll) {
      itemsScroll.addEventListener('scroll', () => checkOverflow(itemsWrapper, itemsScroll));
    }
    if (bottomScroll) {
      bottomScroll.addEventListener('scroll', () => checkOverflow(bottomWrapper, bottomScroll));
    }
  }

  closeDrawer(drawerId) {
    const drawer = document.getElementById(drawerId);
    if (drawer) {
      drawer.classList.remove('active');
      // Wait for animation to complete before hiding
      setTimeout(() => {
        if (!drawer.classList.contains('active')) {
          drawer.setAttribute('hidden', '');
        }
      }, 300);
    }
  }

  async refreshOrderSummary() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Update items list
      const itemsContainer = document.getElementById('order-summary-items');
      if (itemsContainer) {
        let itemsHtml = '';
        cart.items.forEach(item => {
          const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
            ? `<span class="order-summary__item-variant">${item.variant_title}</span>`
            : '';

          // Check for customisation message
          const message = item.properties && item.properties.Message ? item.properties.Message : '';
          const hasMessage = message && message.trim() !== '';

          // Only show customisation for products with custom.customizable metafield set to true
          const isCustomizable = window.customizableProductIds && window.customizableProductIds.includes(item.product_id);

          const customisationHtml = isCustomizable ? `
            <div class="order-summary__item-customisation" data-customisation-wrapper>
              ${hasMessage ? `
                <div class="order-summary__customisation-display" data-customisation-display>
                  <span class="order-summary__customisation-text" data-customisation-text>${message}</span>
                  <button type="button" class="order-summary__customisation-edit" data-edit-customisation aria-label="Edit customisation">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  </button>
                </div>
              ` : `
                <button type="button" class="order-summary__customisation-add" data-add-customisation>
                  Customise?
                </button>
              `}
              <div class="order-summary__customisation-form" data-customisation-form hidden>
                <span class="order-summary__customisation-hint">Our fondant letters are available in English only</span>
                <div class="order-summary__customisation-input-row">
                  <textarea
                    class="order-summary__customisation-input"
                    data-customisation-input
                    rows="1"
                    maxlength="30"
                    placeholder="Write your message in fondant!"
                  >${message}</textarea>
                  <button type="button" class="order-summary__customisation-save" data-save-customisation>Save</button>
                </div>
                <span class="order-summary__customisation-counter" data-customisation-counter>${message.length}/30</span>
                ${hasMessage ? `<button type="button" class="order-summary__customisation-cancel" data-cancel-customisation>Cancel</button>` : ''}
              </div>
            </div>
          ` : '';

          itemsHtml += `
            <div class="order-summary__item" data-item-key="${item.key}" data-variant-id="${item.variant_id}">
              <div class="order-summary__item-image">
                ${item.image ? `<img src="${item.image.replace(/(\.[^.]+)$/, '_100x100$1')}" alt="${item.title}" width="50" height="50">` : ''}
              </div>
              <div class="order-summary__item-details">
                <span class="order-summary__item-title">${item.product_title}</span>
                ${variantTitle}
                <span class="order-summary__item-qty">Qty: ${item.quantity}</span>
                ${customisationHtml}
              </div>
              <div class="order-summary__item-price">
                <span class="money">${this.formatMoney(item.final_line_price)}</span>
              </div>
            </div>
          `;
        });
        itemsContainer.innerHTML = itemsHtml;
      }

      // Update order details
      const detailsContainer = document.getElementById('order-summary-details');
      if (detailsContainer) {
        let detailsHtml = '';

        // Delivery date
        const deliveryDate = this.deliveryDate || cart.attributes.delivery_date;
        if (deliveryDate) {
          detailsHtml += `
            <div class="order-summary__detail">
              <span>Delivery Date</span>
              <span>${this.formatDateHuman(deliveryDate)}</span>
            </div>
          `;
        }

        // Gift order info
        if (cart.attributes.gift_order === 'true') {
          detailsHtml += `
            <div class="order-summary__detail">
              <span>Gift Order</span>
              <span>Yes</span>
            </div>
          `;

          // Receiver info
          if (cart.attributes.receiver_type === 'someone-else') {
            const receiverName = [cart.attributes.receiver_first_name, cart.attributes.receiver_last_name].filter(Boolean).join(' ');
            if (receiverName) {
              detailsHtml += `
                <div class="order-summary__detail">
                  <span>Recipient</span>
                  <span>${receiverName}</span>
                </div>
              `;
            }
            if (cart.attributes.receiver_phone) {
              detailsHtml += `
                <div class="order-summary__detail">
                  <span>Recipient Phone</span>
                  <span>${cart.attributes.receiver_phone}</span>
                </div>
              `;
            }
            // Sender info
            const senderName = [cart.attributes.sender_first_name, cart.attributes.sender_last_name].filter(Boolean).join(' ');
            if (senderName) {
              detailsHtml += `
                <div class="order-summary__detail">
                  <span>From</span>
                  <span>${senderName}</span>
                </div>
              `;
            }
          }

          // Gift message
          if (cart.attributes.gift_message) {
            detailsHtml += `
              <div class="order-summary__detail order-summary__detail--message">
                <span>Gift Message</span>
                <span>"${cart.attributes.gift_message}"</span>
              </div>
            `;
          }
        }

        detailsContainer.innerHTML = detailsHtml;
      }

      // Show/hide delivery note based on receiver type
      const deliveryNote = document.querySelector('.order-summary__delivery-note');
      if (deliveryNote) {
        deliveryNote.style.display = cart.attributes.receiver_type === 'someone-else' ? '' : 'none';
      }

      // Update subtotal
      const subtotalEl = document.getElementById('order-summary-subtotal');
      if (subtotalEl) {
        subtotalEl.textContent = this.formatMoney(cart.total_price);
      }
    } catch (error) {
      console.error('Error refreshing order summary:', error);
    }
  }

  closeAllDrawers() {
    // Close all checkout drawers in reverse order for proper stacking animation
    const allSteps = [...new Set([...this.giftingSteps, ...this.nonGiftingSteps])];
    allSteps.reverse().forEach(drawerId => {
      this.closeDrawer(drawerId);
    });
    this.currentStep = -1;
  }

  closeEverything() {
    // Close all checkout drawers
    this.closeAllDrawers();
    // Close cart drawer
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.close();
    }
  }

  initCalendar() {
    const calendar = document.getElementById('delivery-calendar');
    if (!calendar) return;

    // Render initial calendar
    this.renderCalendar();

    // Handle navigation clicks
    const prevBtn = calendar.querySelector('[data-calendar-prev]');
    const nextBtn = calendar.querySelector('[data-calendar-next]');

    if (prevBtn) {
      if (this.boundHandlers.calendarPrev) {
        prevBtn.removeEventListener('click', this.boundHandlers.calendarPrev);
      }
      this.boundHandlers.calendarPrev = () => {
        this.calendarDate.setMonth(this.calendarDate.getMonth() - 1, 1);
        this.renderCalendar();
      };
      prevBtn.addEventListener('click', this.boundHandlers.calendarPrev);
    }

    if (nextBtn) {
      if (this.boundHandlers.calendarNext) {
        nextBtn.removeEventListener('click', this.boundHandlers.calendarNext);
      }
      this.boundHandlers.calendarNext = () => {
        this.calendarDate.setMonth(this.calendarDate.getMonth() + 1, 1);
        this.renderCalendar();
      };
      nextBtn.addEventListener('click', this.boundHandlers.calendarNext);
    }

    // Handle day clicks via delegation
    const daysContainer = document.getElementById('calendar-days');
    if (daysContainer) {
      if (this.boundHandlers.calendarDayClick) {
        daysContainer.removeEventListener('click', this.boundHandlers.calendarDayClick);
      }
      this.boundHandlers.calendarDayClick = (e) => {
        const dayBtn = e.target.closest('.delivery-calendar__day');
        if (dayBtn && !dayBtn.disabled) {
          this.selectCalendarDate(dayBtn.dataset.date);
        }
      };
      daysContainer.addEventListener('click', this.boundHandlers.calendarDayClick);
    }
  }

  renderCalendar() {
    const monthYearEl = document.getElementById('calendar-month-year');
    const daysContainer = document.getElementById('calendar-days');
    if (!monthYearEl || !daysContainer) return;

    const year = this.calendarDate.getFullYear();
    const month = this.calendarDate.getMonth();

    // Update month/year display
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    monthYearEl.textContent = `${monthNames[month]} ${year}`;

    // Get first day of month and total days
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // Get today for comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Build days HTML
    let html = '';

    // Empty cells for days before first of month
    for (let i = 0; i < firstDay; i++) {
      html += '<span class="delivery-calendar__day delivery-calendar__day--empty"></span>';
    }

    // Day buttons
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = this.formatDate(date);
      const isPastOrToday = date <= today; // Disable today as well - delivery from tomorrow onwards
      const isToday = date.getTime() === today.getTime();
      const isSelected = this.deliveryDate === dateStr;

      let classes = 'delivery-calendar__day';
      if (isToday) classes += ' is-today';
      if (isSelected) classes += ' is-selected';

      html += `<button type="button" class="${classes}" data-date="${dateStr}" ${isPastOrToday ? 'disabled' : ''}>${day}</button>`;
    }

    daysContainer.innerHTML = html;
  }

  selectCalendarDate(dateStr) {
    // Validate against restrictions
    if (window.deliveryRestrictions) {
      const selectedDate = new Date(dateStr + 'T00:00:00');
      if (!window.deliveryRestrictions.isDateAllowed(selectedDate)) {
        const message = window.deliveryRestrictions.getBlockedDateMessage(selectedDate) ||
                        'This date is not available for delivery';
        window.deliveryRestrictions.showDateError(message);
        return;
      }
    }

    // Clear any error messages on successful selection
    this.clearDateError();
    this.deliveryDate = dateStr;

    // Update hidden input
    const hiddenInput = document.getElementById('delivery-date-input');
    if (hiddenInput) hiddenInput.value = dateStr;

    // Re-render to show selection
    this.renderCalendar();
  }

  formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  async handleUpsellAdd(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    // Disable button and show loading state
    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = 'Adding...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId),
            quantity: 1
          }]
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add item');
      }

      // Update button to show success
      button.textContent = 'Added!';
      button.classList.add('is-added');

      // Update the free shipping bar
      this.updateFreeShippingBar();

      // Refresh the cart drawer
      if (window.cartDrawer && typeof window.cartDrawer.renderContents === 'function') {
        // Fetch updated cart and re-render
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        // Trigger cart update event that Shopify themes listen to
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      }

      // Reset button after delay
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
        button.classList.remove('is-added');
      }, 2000);

    } catch (error) {
      console.error('Error adding to cart:', error);
      button.textContent = 'Error';
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 2000);
    }
  }

  async updateFreeShippingBar() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const bar = document.querySelector('.free-shipping-bar');
      if (!bar) return;

      const threshold = parseInt(bar.dataset.threshold) || 56000;
      const cartTotal = cart.total_price;
      const remaining = threshold - cartTotal;
      const progressPercent = Math.min((cartTotal / threshold) * 100, 100);

      // Update message
      const messageEl = bar.querySelector('.free-shipping-bar__message');
      if (messageEl) {
        if (remaining > 0) {
          messageEl.innerHTML = `You're <strong><span class="money">${this.formatMoney(remaining)}</span></strong> away from free delivery!`;
          messageEl.classList.remove('free-shipping-bar__message--success');
        } else {
          messageEl.innerHTML = `You've unlocked <strong>free delivery!</strong>`;
          messageEl.classList.add('free-shipping-bar__message--success');
        }
      }

      // Update progress bar
      const fillEl = bar.querySelector('.free-shipping-bar__fill');
      if (fillEl) {
        fillEl.style.width = `${progressPercent}%`;
      }

      // Update marker position
      const markerEl = bar.querySelector('.free-shipping-bar__marker');
      if (markerEl) {
        markerEl.style.left = `${progressPercent}%`;
      }
    } catch (error) {
      console.error('Error updating free shipping bar:', error);
    }
  }

  showCustomisationForm(wrapper) {
    const display = wrapper.querySelector('[data-customisation-display]');
    const addBtn = wrapper.querySelector('[data-add-customisation]');
    const form = wrapper.querySelector('[data-customisation-form]');

    if (display) display.setAttribute('hidden', '');
    if (addBtn) addBtn.setAttribute('hidden', '');
    if (form) {
      form.removeAttribute('hidden');
      const textarea = form.querySelector('[data-customisation-input]');
      if (textarea) textarea.focus();
    }
  }

  hideCustomisationForm(wrapper) {
    const display = wrapper.querySelector('[data-customisation-display]');
    const addBtn = wrapper.querySelector('[data-add-customisation]');
    const form = wrapper.querySelector('[data-customisation-form]');
    const textarea = wrapper.querySelector('[data-customisation-input]');
    const originalText = wrapper.querySelector('[data-customisation-text]');

    // Reset textarea to original value if exists
    if (textarea && originalText) {
      textarea.value = originalText.textContent;
    } else if (textarea) {
      textarea.value = '';
    }

    if (form) form.setAttribute('hidden', '');

    // Show appropriate element based on whether there's existing customisation
    if (originalText && originalText.textContent.trim()) {
      if (display) display.removeAttribute('hidden');
    } else {
      if (addBtn) addBtn.removeAttribute('hidden');
    }
  }

  async saveCustomisation(item, wrapper) {
    const itemKey = item.dataset.itemKey;
    const variantId = item.dataset.variantId;
    const textarea = wrapper.querySelector('[data-customisation-input]');
    const saveBtn = wrapper.querySelector('[data-save-customisation]');
    const newMessage = textarea ? textarea.value.trim() : '';

    if (!saveBtn) return;

    // Disable button during save
    saveBtn.disabled = true;
    saveBtn.textContent = 'Saving...';

    try {
      // Get current cart
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Find item by key or variant_id
      let cartItem = cart.items.find(i => i.key === itemKey);
      if (!cartItem && variantId) {
        cartItem = cart.items.find(i => String(i.variant_id) === String(variantId));
      }

      if (!cartItem) {
        throw new Error('Item not found in cart');
      }

      // Update properties with new message
      const updatedProperties = { ...cartItem.properties, 'Message': newMessage || '' };

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: cartItem.key,
          quantity: cartItem.quantity,
          properties: updatedProperties
        })
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.description || 'Failed to update cart');
      }

      const result = await response.json();

      // Update the item key in DOM for future edits
      const updatedItem = result.items.find(i => String(i.variant_id) === String(cartItem.variant_id));
      if (updatedItem) {
        item.dataset.itemKey = updatedItem.key;
      }

      // Update the display
      this.updateCustomisationDisplay(wrapper, newMessage);

      // Trigger cart update for main cart drawer
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: result });
      }

    } catch (error) {
      console.error('Error saving customisation:', error);
      alert(error.message || 'Failed to save. Please try again.');
    } finally {
      saveBtn.disabled = false;
      saveBtn.textContent = 'Save';
    }
  }

  updateCustomisationDisplay(wrapper, message) {
    const form = wrapper.querySelector('[data-customisation-form]');
    const display = wrapper.querySelector('[data-customisation-display]');
    const addBtn = wrapper.querySelector('[data-add-customisation]');

    // Hide form
    if (form) form.setAttribute('hidden', '');

    if (message) {
      // Has message - show display with edit button
      if (addBtn) addBtn.remove();

      if (display) {
        // Update existing display
        const textEl = display.querySelector('[data-customisation-text]');
        if (textEl) textEl.textContent = message;
        display.removeAttribute('hidden');
      } else {
        // Create new display
        const newDisplay = document.createElement('div');
        newDisplay.className = 'order-summary__customisation-display';
        newDisplay.setAttribute('data-customisation-display', '');
        newDisplay.innerHTML = `
          <span class="order-summary__customisation-text" data-customisation-text>${message}</span>
          <button type="button" class="order-summary__customisation-edit" data-edit-customisation aria-label="Edit customisation">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          </button>
        `;
        wrapper.insertBefore(newDisplay, form);
      }
    } else {
      // No message - show add button
      if (display) display.setAttribute('hidden', '');

      if (!addBtn) {
        const newAddBtn = document.createElement('button');
        newAddBtn.type = 'button';
        newAddBtn.className = 'order-summary__customisation-add';
        newAddBtn.setAttribute('data-add-customisation', '');
        newAddBtn.textContent = 'Customise?';
        wrapper.insertBefore(newAddBtn, form);
      } else {
        addBtn.removeAttribute('hidden');
      }
    }
  }

  formatMoney(fils) {
    // Format fils to Dirhams - the currency symbol is added via CSS ::before on .money class.
    // Trim trailing .00 to match the store's no-fils display convention.
    const amount = (fils / 100).toFixed(2);
    return amount.replace(/\.00$/, '');
  }

  formatDateHuman(dateStr) {
    // Convert YYYY-MM-DD to human-friendly format like "Thursday, 28 November 2025"
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr + 'T00:00:00');
      const options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
      return date.toLocaleDateString('en-GB', options);
    } catch (e) {
      return dateStr;
    }
  }

  showDateError(message) {
    const datePickerContainer = document.querySelector('.delivery-date-picker');
    if (!datePickerContainer) return;

    // Remove any existing error
    this.clearDateError();

    // Create error element
    const errorEl = document.createElement('div');
    errorEl.className = 'delivery-date-error';
    errorEl.textContent = message;

    // Find best place to insert
    const calendar = document.getElementById('delivery-calendar');
    const mobileInput = document.getElementById('delivery-date-input-mobile');

    if (calendar && window.innerWidth > 768) {
      calendar.parentNode.insertBefore(errorEl, calendar.nextSibling);
    } else if (mobileInput) {
      mobileInput.parentNode.insertBefore(errorEl, mobileInput.nextSibling);
    } else {
      datePickerContainer.appendChild(errorEl);
    }
  }

  clearDateError() {
    const existingError = document.querySelector('.delivery-date-error');
    if (existingError) {
      existingError.remove();
    }
  }
}

// Initialize when DOM is ready and store as global instance
window.checkoutDrawerFlow = null;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.checkoutDrawerFlow = new CheckoutDrawerFlow();
  });
} else {
  window.checkoutDrawerFlow = new CheckoutDrawerFlow();
}

// Listen for cart updates to reset state when cart becomes empty
document.addEventListener('cart:updated', (event) => {
  if (window.checkoutDrawerFlow && event.detail?.cartData) {
    const cartData = event.detail.cartData;
    const isEmpty = cartData.item_count === 0 || (cartData.items && cartData.items.length === 0);
    if (isEmpty) {
      window.checkoutDrawerFlow.resetState();
      window.checkoutDrawerFlow.closeAllDrawers();
      // Clear cart attributes to prevent stale data on server
      window.checkoutDrawerFlow.clearAllCartAttributes();
    }
  }
});
